import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/shared/api/apiClient'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  PACKAGING_SUPPLIERS_KEY,
  type PackagingItem,
} from '@/shared/hooks/api/packagingSuppliers'

type LexAssignPackagingItemSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: string
  packagedItemId: string
  assignedPackagingItemIds: ReadonlySet<string>
  packagingItems: PackagingItem[]
}

export function LexAssignPackagingItemSheet({
  open,
  onOpenChange,
  supplierId,
  packagedItemId,
  assignedPackagingItemIds,
  packagingItems,
}: LexAssignPackagingItemSheetProps) {
  const { t } = useTranslation('packagingSuppliers')
  const assignPackagingItem = useAssignExistingPackagingToPart(
    supplierId,
    packagedItemId,
    packagingItems
  )

  const availablePackagingItems = useMemo(() => {
    return packagingItems.filter(
      (packagingItem) => !assignedPackagingItemIds.has(packagingItem.id)
    )
  }, [assignedPackagingItemIds, packagingItems])

  const emptyPackagingMessage =
    packagingItems.length === 0
      ? t('details.packagingCatalog.assignPackagingItemEmptyNoItems')
      : t('details.packagingCatalog.assignPackagingItemEmptyAllLinkedToPart')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md'
      >
        <div className='flex min-h-0 flex-1 flex-col'>
          <SheetHeader className='shrink-0 border-b px-6 py-4 text-start'>
            <SheetTitle>
              {t('details.packagingCatalog.assignPackagingItemTitle')}
            </SheetTitle>
          </SheetHeader>
          <div className='flex-1 space-y-2 overflow-y-auto px-6 py-4'>
            {availablePackagingItems.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                {emptyPackagingMessage}
              </p>
            ) : (
              availablePackagingItems.map((packagingItem) => (
                <button
                  key={packagingItem.id}
                  type='button'
                  className='flex w-full flex-col rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/50'
                  disabled={assignPackagingItem.isPending}
                  onClick={() => {
                    void (async () => {
                      await assignPackagingItem.mutateAsync(packagingItem.id)
                      onOpenChange(false)
                    })()
                  }}
                >
                  <span className='font-medium'>{packagingItem.name}</span>
                  <span className='text-muted-foreground'>
                    {packagingItem.sku?.trim() || t('common:notAvailable')}
                  </span>
                </button>
              ))
            )}
          </div>
          <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('common:actions.cancel')}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function useAssignExistingPackagingToPart(
  supplierId: string,
  packagedItemId: string,
  packagingItems: PackagingItem[]
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (packagingItemId: string) => {
      const sourceItem = packagingItems.find(
        (row) => row.id === packagingItemId
      )
      const existingIds =
        sourceItem?.packagedItems?.map((part) => part.id) ?? []
      if (existingIds.includes(packagedItemId)) {
        return
      }
      await apiClient.patch(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${packagingItemId}`,
        {
          packagesOwnProduct: true,
          packagedItemIds: [...existingIds, packagedItemId],
        }
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}
